import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Users, Trash2, Mail, UserCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ROLE_LABEL = {
  owner: 'Owner',
  co_owner: 'Co-owner (full access)',
  viewer: 'Viewer (read only)',
};

const roleColor = (role) => {
  if (role === 'owner') return 'bg-slate-900 text-white';
  if (role === 'co_owner') return 'bg-indigo-100 text-indigo-800';
  return 'bg-slate-100 text-slate-700';
};

const WealthHousehold = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/household`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load household');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error('Enter a valid email');
      return;
    }
    try {
      await axios.post(`${API}/household/invite`, { email: inviteEmail, role: inviteRole });
      toast.success('Invitation sent');
      setInviteEmail('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invite failed');
    }
  };

  const changeRole = async (email, role) => {
    try {
      await axios.put(`${API}/household/members/${encodeURIComponent(email)}`, { role });
      toast.success('Role updated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`Remove ${email} from your household?`)) return;
    try {
      await axios.delete(`${API}/household/members/${encodeURIComponent(email)}`);
      toast.success('Member removed');
      load();
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  if (loading) {
    return <div className="p-8 md:p-12 font-chivo text-xl text-slate-900" data-testid="household-loading">Loading household…</div>;
  }

  return (
    <div className="p-8 md:p-12" data-testid="wealth-household-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">Household</h1>
            <p className="text-slate-600 mt-1 font-ibm">Share your portfolio with your spouse or family — read-only or co-ownership.</p>
          </div>
          <div className={`px-4 py-2 rounded-sm text-xs font-ibm font-semibold uppercase tracking-widest ${roleColor(data?.my_role)}`} data-testid="my-role-badge">
            Your role: {ROLE_LABEL[data?.my_role] || 'owner'}
          </div>
        </div>

        {!data?.is_owner && (
          <div className="mb-6 bg-indigo-50 border-l-4 border-indigo-500 p-5 rounded-sm" data-testid="joined-household-banner">
            <p className="font-ibm text-slate-800">
              You've been added to another household as a <strong>{ROLE_LABEL[data.my_role]}</strong>. All wealth data on the other pages belongs to the household owner.
            </p>
          </div>
        )}

        {data?.is_owner && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6" data-testid="invite-form">
            <h2 className="text-sm font-chivo font-bold uppercase tracking-widest text-slate-900 mb-4">Invite a member</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  data-testid="invite-email-input"
                />
              </div>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer (read only)</SelectItem>
                  <SelectItem value="co_owner">Co-owner (full access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={invite} className="bg-slate-900 text-white rounded-sm hover:bg-slate-800" data-testid="send-invite-btn">
                <Mail size={14} className="mr-2" />Send invite
              </Button>
              <p className="text-xs font-ibm text-slate-500">They just log in with the invited email — no activation link needed.</p>
            </div>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <h2 className="text-sm font-chivo font-bold uppercase tracking-widest text-slate-900 mb-4 flex items-center gap-2">
            <Users size={16} /> Members
          </h2>
          {(data?.members || []).length === 0 ? (
            <p className="text-sm font-ibm text-slate-500 py-6 text-center" data-testid="no-members">
              {data?.is_owner ? 'No members yet. Invite someone above.' : 'Household owner has not added other members yet.'}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="members-list">
              {data.members.map((m) => (
                <li key={m.email} className="flex items-center justify-between py-3" data-testid={`member-${m.email}`}>
                  <div className="flex items-center gap-3">
                    <UserCircle size={28} className="text-slate-400" />
                    <div>
                      <p className="font-ibm font-medium text-slate-900">{m.name || m.email}</p>
                      <p className="text-xs text-slate-500 font-ibm">
                        {m.email} · <span className="uppercase tracking-widest">{m.status}</span>
                      </p>
                    </div>
                  </div>
                  {data.is_owner ? (
                    <div className="flex items-center gap-2">
                      <Select value={m.role} onValueChange={(v) => changeRole(m.email, v)}>
                        <SelectTrigger className="w-44" data-testid={`role-select-${m.email}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer</SelectItem>
                          <SelectItem value="co_owner">Co-owner</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => remove(m.email)} data-testid={`remove-${m.email}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  ) : (
                    <span className={`px-3 py-1 text-xs font-ibm font-semibold uppercase tracking-widest rounded-sm ${roleColor(m.role)}`}>
                      {ROLE_LABEL[m.role]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default WealthHousehold;
